<template>
  <li
    :data-cy="`source-options-${source.id}`"
    class="flex h-full flex-col overflow-hidden"
  >
    <div
      class="flex w-full cursor-pointer items-center justify-between px-2"
      @click="isOpen = !isOpen"
    >
      <span class="text-gray-400">{{ source.title }}</span>

      <div class="flex items-center space-x-2">
        <div class="h-4 w-5">
          <loader :is-loading="isLoading" size="20" />
        </div>
        <icon-chevron-up
          class="transform transition-all"
          :class="{ 'rotate-180': !!isOpen }"
          :fill="arrowColor"
        />
      </div>
    </div>

    <ul
      v-if="isOpen"
      class="custom-scrollbar mt-2 flex max-h-50 flex-col space-y-2 overflow-x-hidden overflow-y-auto"
    >
      <li
        v-show="!isLoading"
        v-for="(option, optionKey) in renderOptions"
        :key="`${source.id}-option-${String(optionKey)}`"
        class="flex h-10 items-center justify-between rounded-sm p-2 text-gray-500 hover:bg-blue-50"
        :class="
          getOptionId(option, optionKey) === selectedValue.id
            ? 'bg-blue-50'
            : 'bg-white'
        "
        @click="selectValue(source, option, optionKey)"
      >
        <slot name="option" :option="option" :key="optionKey">
          <span class="overflow-hidden text-ellipsis whitespace-nowrap">
            {{
              typeof option === "object"
                ? option?.[
                    (source.propertyToRender as keyof typeof option) ?? "name"
                  ]
                : option
            }}
          </span>
        </slot>

        <div v-if="getOptionId(option, optionKey) === selectedValue.id">
          <icon-base :icon="IconCheckmark" class="text-blue-500" />
        </div>
      </li>

      <li
        v-if="!Object.keys(renderOptions).length"
        class="flex h-10 items-center rounded-sm p-2 text-gray-500"
      >
        {{ $t("COMMON.NO_OPTIONS_AVAILABLE") }}
      </li>

      <li
        v-if="canLoadMore"
        class="flex items-center justify-center border-t pt-2"
      >
        <a
          class="cursor-pointer text-sm"
          :class="isLoading ? 'text-gray-400' : 'text-primary'"
          @click="!isLoading && loadMore()"
        >
          {{ $t("COMMON.LOAD_MORE") }}
        </a>
      </li>
    </ul>
  </li>
</template>

<script
  setup
  lang="ts"
  generic="
    TData extends { id: string | number },
    TResponse extends {
      data: TData[];
      meta: { current_page: number; last_page: number };
    },
    TSourceCallback extends (params?: any) => Promise<TResponse>,
    TSourceObject extends Record<string, string>
  "
>
import { computed, onMounted, ref, watch } from "vue";

import Fuse from "fuse.js";

import { AnalyticsEvent } from "@/enums/analytics";
import { useAnalyticsStore } from "@/stores/analytics";

import IconCheckmark from "@/components/icons/IconCheckmarkSmallWhite.vue";
import IconChevronUp from "@/components/icons/deals/IconChevronUp.vue";
import IconBase from "@/components/ui/IconBase.vue";
import Loader from "@/components/ui/Loader.vue";

type TModelValue = { id: string | number; label: string; source: string };

type TSource = {
  id: string;
  title: string;
  options: TSourceCallback | TSourceObject;
  propertyToRender?: string;
};

const { source, searchTerm } = defineProps<{
  source: TSource;
  selectedValue: TModelValue;
  searchTerm: string;
}>();

const emit = defineEmits<{
  "update:selectedValue": [TModelValue];
}>();

const { trackEvent } = useAnalyticsStore();

const arrowColor = "#99a1af";
const isOpen = ref(false);
const isLoading = ref(false);
const loadedOptions = ref<TResponse | TSourceObject>({} as TSourceObject);

const isSourceOptionFunction = (
  options: TSourceCallback | TSourceObject
): options is TSourceCallback => typeof options === "function";

const renderOptions = computed(() => {
  return isSourceOptionFunction(source.options)
    ? (loadedOptions.value?.data ?? [])
    : loadedOptions.value;
});

const canLoadMore = computed(() => {
  const optionResponse = loadedOptions.value;
  return (
    optionResponse &&
    Object.prototype.hasOwnProperty.call(optionResponse, "meta") &&
    (optionResponse as TResponse).meta.current_page <
      (optionResponse as TResponse).meta.last_page
  );
});

const getOptionId = (option: unknown, optionKey: unknown) => {
  return isSourceOptionFunction(source.options)
    ? (option as TData).id
    : optionKey;
};

const selectValue = (source: TSource, option: unknown, optionKey: unknown) => {
  const newValue = {
    id: isSourceOptionFunction(source.options)
      ? (option as TData).id
      : (optionKey as string),
    label:
      typeof option === "object"
        ? (option?.[
            (source.propertyToRender as keyof typeof option) ?? "name"
          ] as unknown as string)
        : (option as string),
    source: source.id,
  };

  emit("update:selectedValue", newValue);
  if (source.id === "docusignTemplate") {
    trackEvent(AnalyticsEvent.SelectedTemplateToGenerateResumeApplicationLink, {
      type: source.id,
    });
  }
};

const loadMore = async (options?: { append?: boolean }) => {
  isLoading.value = true;
  const appendOptions = options?.append ?? true;
  const getPaginatedOptions =
    isSourceOptionFunction(source.options) &&
    (source.options as TSourceCallback);

  if (!source || !getPaginatedOptions) {
    return;
  }

  const paginatedOptionsMeta = (loadedOptions.value as TResponse | undefined)
    ?.meta;

  const currentPage =
    appendOptions && paginatedOptionsMeta
      ? paginatedOptionsMeta.current_page
      : 0;
  const nextPage = currentPage + 1;
  try {
    const response = await getPaginatedOptions({
      page: nextPage,
      search: searchTerm,
    });

    if (appendOptions) {
      loadedOptions.value.meta = response.meta;
      if (!loadedOptions.value.data) {
        loadedOptions.value.data = [];
      }
      (loadedOptions.value as TResponse).data.push(...response.data);
    } else {
      loadedOptions.value = response;
    }
  } finally {
    isLoading.value = false;
  }
};

const searchOptions = async (searchValue: string) => {
  const search = searchValue.trim().toLowerCase();
  const sourceOptionsIsFunction = isSourceOptionFunction(source.options);

  if (sourceOptionsIsFunction) {
    await loadMore({ append: false });
  } else {
    if (search.length) {
      const options = Object.keys(source.options).map((key) => ({
        id: key,
        value: (source.options as TSourceObject)[key],
      }));
      const fuse = new Fuse(options, { keys: ["value"], threshold: 0.4 });

      loadedOptions.value = fuse
        .search(search)
        .reduce<Record<string, string>>((acc, { item: { id, value } }) => {
          acc[id] = value;
          return acc;
        }, {}) as TSourceObject;
    } else {
      loadedOptions.value = source.options as TSourceObject;
    }
  }

  if (search.length) {
    isOpen.value = sourceOptionsIsFunction
      ? !!loadedOptions.value.data.length
      : !!Object.keys(loadedOptions.value).length;
  } else {
    isOpen.value = false;
  }
};

const initDropdownOptions = async () => {
  if (isSourceOptionFunction(source.options)) {
    loadMore();
  } else {
    loadedOptions.value = source.options;
  }
};

watch(() => searchTerm, searchOptions);

onMounted(() => {
  initDropdownOptions();
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.custom-scrollbar {
  &::-webkit-scrollbar {
    @apply w-1 bg-transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply h-6 w-full rounded bg-gray-300;
  }
}
</style>
